/**
 * ★ "PREVIEW YOUR PUBLIC PAGE" AND "OPEN YOUR PUBLIC PAGE" (design 24, T28 - the owner's pick).
 *
 * The teammate filled in the required fields and found no way to see how it looks. Two links, at
 * the top of the side column, so they stay in view beside the form:
 *   - PREVIEW, for everybody - an applicant too, before any public page exists: the cabinet's own
 *     preview page (src/routes/profile-preview/), drawn from the saved profile with only what is
 *     marked public. A real `<a href>`, so a new tab or a copied address lands on it; a plain click
 *     moves in place through the app's in-app navigation (features/shell/in-app-navigation.ts),
 *     the router in the app and a full page load without one.
 *   - OPEN, for an approved author with an address: the live page on the website, in a new tab, as
 *     the menu's "Your public page" opens it (features/shell/CabinetShell.tsx). Only what is saved
 *     and public shows there, and only once the website draws it - hence the preview beside it.
 */
import { Button, Stack } from '@mantine/core';
import { ExternalLink, Eye } from '@moonx/ui';
import type { MouseEvent, ReactNode } from 'react';
import { useTranslation } from '../../../i18n';
import { WEBSITE_ORIGIN } from '../../auth/handoff/website-handoff';
import { followsInApp, useNavigateToHref } from '../../shell/in-app-navigation';
import { PROFILE_PREVIEW_PATH } from '../../../routes/profile-preview/paths';
import { SideCard } from './PageFrame';

export interface PublicPageLinksProps {
  /** The page address, when there is one. */
  readonly slug: string | null | undefined;
  /** True for an approved author, whose public page is live. */
  readonly live: boolean;
}

export function PublicPageLinks({ slug, live }: PublicPageLinksProps): ReactNode {
  const { t } = useTranslation('application');
  const navigate = useNavigateToHref();
  const address = (slug ?? '').trim();

  const followInApp = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (!followsInApp(event)) {
      return;
    }
    event.preventDefault();
    navigate(PROFILE_PREVIEW_PATH);
  };

  return (
    <SideCard>
      <Stack gap="xs">
        <Button
          component="a"
          href={PROFILE_PREVIEW_PATH}
          onClick={followInApp}
          variant="default"
          radius="xl"
          size="sm"
          leftSection={<Eye size={15} aria-hidden />}
        >
          {t('publicPage.preview')}
        </Button>
        {live && address !== '' ? (
          <Button
            component="a"
            href={`${WEBSITE_ORIGIN}/authors/${encodeURIComponent(address)}`}
            target="_blank"
            rel="noopener"
            variant="subtle"
            radius="xl"
            size="sm"
            leftSection={<ExternalLink size={15} aria-hidden />}
          >
            {t('publicPage.open')}
          </Button>
        ) : null}
      </Stack>
    </SideCard>
  );
}
